import { BaseService } from "./base-service";
import { API_BASE_URL } from "configuration/config";

const CATALOGICAL_PROXY_PREFIX = "/cat-";
const PAGE_SIZE = 500;
const CACHE_TTL_MS = 4 * 60 * 60 * 1000; // 4 hours
const CACHE_KEY_PREFIX = "catalogical_";

interface CacheEntry<T> {
    data: T[];
    expiresAt: number;
}

class CatalogicalConfigService extends BaseService {
    constructor() {
        super(API_BASE_URL);
    }

    async getItems<T>(domain: string): Promise<T[]> {
        const cached = this.getFromCache<T>(domain);
        if (cached) return cached;

        const firstPage = await this.fetchPage<T>(domain, 0);
        const total = firstPage.total;
        let allItems: T[];

        if (total <= PAGE_SIZE) {
            allItems = firstPage.items;
        } else {
            allItems = [...firstPage.items];
            for (let offset = PAGE_SIZE; offset < total; offset += PAGE_SIZE) {
                const page = await this.fetchPage<T>(domain, offset);
                allItems.push(...page.items);
            }
        }

        this.setCache(domain, allItems);
        return allItems;
    }

    private getFromCache<T>(domain: string): T[] | null {
        try {
            const raw = localStorage.getItem(`${CACHE_KEY_PREFIX}${domain}`);
            if (!raw) return null;
            const entry: CacheEntry<T> = JSON.parse(raw);
            if (Date.now() > entry.expiresAt) {
                localStorage.removeItem(`${CACHE_KEY_PREFIX}${domain}`);
                return null;
            }
            return entry.data;
        } catch (e) {
            console.error(`[CatalogicalConfigService] Failed to read cache for ${domain}:`, e);
            return null;
        }
    }

    private setCache<T>(domain: string, data: T[]): void {
        try {
            const entry: CacheEntry<T> = { data, expiresAt: Date.now() + CACHE_TTL_MS };
            localStorage.setItem(`${CACHE_KEY_PREFIX}${domain}`, JSON.stringify(entry));
        } catch (e) {
            console.error(`[CatalogicalConfigService] Failed to write cache for ${domain}:`, e);
        }
    }

    private async fetchPage<T>(domain: string, offset: number): Promise<{ items: T[]; total: number }> {
        const response = await this.fetch(
            `${CATALOGICAL_PROXY_PREFIX}${domain}/items/${domain}?limit=${PAGE_SIZE}&offset=${offset}`,
        );
        if (!response.ok) {
            throw new Error(`Failed to fetch ${domain} items: ${response.status}`);
        }
        const data = await response.json();
        return { items: data.data ?? [], total: data.pagination?.total ?? 0 };
    }
}

export const catalogicalConfigService = new CatalogicalConfigService();
